/**
 * Fix the record (SYR-111 phase 3).
 *
 * The one class of finding a brand can act on directly: the models read the
 * public record, so the public record is the lever. Each card commits to the
 * Action Plan under a stable id, so a fix keeps its place when next month's
 * read words it differently.
 *
 * Built to the same shape as the Briefing's RecommendationCard (accent
 * border, badge, title, rationale, numbered objectives with chips, a toggle
 * foot) so a marketer who knows one knows the other. What a record fix adds
 * is its ground (why this is a record problem and not a brand problem), its
 * level, and the pages the models read.
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReadSection } from './ReadSection';
import { CheckOutlined } from '@ant-design/icons';
import { ObjectiveChips } from '../ObjectiveChips';
import { ObjectiveAskExpert } from '../ObjectiveAskExpert';

export interface RecordFixEntry {
  card: {
    element?: string;
    ground?: string;
    title?: string;
    rationale?: string;
    objectives?: { task: string; detail?: string; impact?: string; effort?: string; moves?: string }[];
    citations?: string[];
    level?: string;
  };
  playId: string;
  added: boolean;
  done: number[];
}

interface Props {
  fixes: RecordFixEntry[];
  // Turns an objective's `moves` element into its current score, so the framed
  // question carries the number. Absent in the admin preview, where the link
  // still works and simply asks without one.
  resolve?: (name: string) => any;
  onToggle: (entry: RecordFixEntry) => void;
  busyPlayId?: string | null;
  canEdit?: boolean;
  // The section's number; one later when the read has a pages section.
  n?: string;
}

// Gold, the record-fix colour wherever the badge appears (Action Plan too), and
// distinct from the Briefing's red fixes and green wins.
const ACCENT = '#D97706';

// The ground field holds one of five phrases from the synthesis. Shown in the
// reader's words, because "record moved the models" means nothing cold.
const GROUND_COPY: Record<string, { id: string; defaultMessage: string }> = {
  'record moved the models': {
    id: 'marketerView.recordFix.ground.moved',
    defaultMessage: 'What the web says pulls the models against you on this',
  },
  'customers disagree': {
    id: 'marketerView.recordFix.ground.customers',
    defaultMessage: 'Your customers disagree with what the models believe',
  },
  'cited page names the brand': {
    id: 'marketerView.recordFix.ground.cited',
    defaultMessage: 'A page the models read says this about you',
  },
  // v4: a page the models read most that never speaks about the brand.
  'most-read page leaves the brand out': {
    id: 'marketerView.recordFix.ground.absent',
    defaultMessage: 'A page the models read most does not talk about you',
  },
  // v4: the pages already say it, the models' memory has not caught up.
  'memory lags the pages': {
    id: 'marketerView.recordFix.ground.lags',
    defaultMessage: "The web already says this; the models haven't caught up",
  },
};

export const RecordFixes: React.FunctionComponent<Props> = ({
  fixes,
  resolve,
  onToggle,
  busyPlayId,
  canEdit = true,
  n = '5',
}) => {
  const intl = useIntl();
  if (!fixes?.length) return null;
  const resolveOrNone = resolve ?? (() => undefined);

  return (
    <ReadSection
      id="fix-the-record"
      n={n}
      tone={ACCENT}
      title="What should you fix in the record?"
      sub={intl.formatMessage({
        id: 'marketerView.recordFix.intro',
        defaultMessage:
          'The models believe what they read. These are the changes to the public record that would change what they believe, in order of what the read found. Each one says why it is a record problem rather than a brand problem, and names the pages the models actually read.',
      })}
    >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 18,
            alignItems: 'start',
          }}
        >
          {fixes.map((entry, cardIndex) => {
            const objectives = entry.card.objectives ?? [];
            const ground = entry.card.ground ? GROUND_COPY[entry.card.ground.trim().toLowerCase()] : undefined;
            const busy = busyPlayId === entry.playId;
            return (
              <div
                key={entry.playId}
                style={{
                  background: '#fff',
                  border: `2px solid ${ACCENT}`,
                  borderRadius: 16,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  height: 460,
                  position: 'relative',
                }}
              >
                <div style={{ padding: '20px 22px 6px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: '#fff',
                        padding: '4px 11px',
                        borderRadius: 7,
                        background: ACCENT,
                      }}
                    >
                      <FormattedMessage
                        id="marketerView.recordFix.badge"
                        defaultMessage="RECORD FIX {n}"
                        values={{ n: cardIndex + 1 }}
                      />
                    </span>
                    {entry.card.element && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#64748B',
                          background: '#F1F5F9',
                          border: '1px solid #E2E8F0',
                          borderRadius: 999,
                          padding: '3px 10px',
                        }}
                      >
                        {entry.card.element}
                      </span>
                    )}
                    {entry.card.level && (
                      <span style={{ fontSize: 12, color: '#94A3B8' }}>
                        {entry.card.level === 'page' ? (
                          <FormattedMessage
                            id="marketerView.recordFix.level.page"
                            defaultMessage="specific pages"
                          />
                        ) : (
                          <FormattedMessage
                            id="marketerView.recordFix.level.driver"
                            defaultMessage="across the record"
                          />
                        )}
                      </span>
                    )}
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', margin: '0 0 6px' }}>
                    {entry.card.title}
                  </h3>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '4px 22px 18px' }}>
                  {ground && (
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: '#92400E',
                        background: '#FFFBEB',
                        border: '1px solid #FDE68A',
                        borderRadius: 8,
                        padding: '6px 10px',
                        margin: '0 0 12px',
                      }}
                    >
                      <FormattedMessage id={ground.id} defaultMessage={ground.defaultMessage} />
                    </div>
                  )}
                  {entry.card.rationale && (
                    <p style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.5, margin: '0 0 16px' }}>
                      {entry.card.rationale}
                    </p>
                  )}
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: '#94A3B8',
                      marginBottom: 10,
                    }}
                  >
                    <FormattedMessage
                      id="marketerView.recos.objectives"
                      defaultMessage="Objectives ({n})"
                      values={{ n: objectives.length }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {objectives.map((objective, index) => {
                      const done = entry.done.includes(index);
                      return (
                        <div key={index} style={{ paddingLeft: 18, position: 'relative' }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 5,
                              width: done ? 12 : 6,
                              height: done ? 12 : 6,
                              marginLeft: done ? -3 : 2,
                              borderRadius: '50%',
                              background: done ? '#16A34A' : ACCENT,
                              color: '#fff',
                              fontSize: 8,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {done && <CheckOutlined />}
                          </span>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#0F172A',
                              lineHeight: 1.4,
                              textDecoration: done ? 'line-through' : undefined,
                            }}
                          >
                            {objective.task}
                          </div>
                          {objective.detail && (
                            <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5, marginTop: 3 }}>
                              {objective.detail}
                            </div>
                          )}
                          <ObjectiveChips objective={objective} resolve={resolveOrNone} />
                          <ObjectiveAskExpert
                            cardTitle={entry.card.title}
                            objective={objective as any}
                            resolve={resolve}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {(entry.card.citations ?? []).length > 0 && (
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 16, lineHeight: 1.5 }}>
                      <FormattedMessage
                        id="marketerView.recordFix.from"
                        defaultMessage="Pages the models read: {domains}"
                        values={{ domains: (entry.card.citations ?? []).join(', ') }}
                      />
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  disabled={!canEdit || busy}
                  onClick={() => canEdit && !busy && onToggle(entry)}
                  style={{
                    borderTop: '1px solid #F1F5F9',
                    padding: '14px 22px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    flexShrink: 0,
                    cursor: canEdit ? 'pointer' : 'default',
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: 'left',
                    width: '100%',
                    border: 'none',
                    background: entry.added ? '#ECFDF5' : 'transparent',
                    color: entry.added ? '#047857' : '#64748B',
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      border: `1px solid ${entry.added ? '#16A34A' : '#CBD5E1'}`,
                      background: entry.added ? '#16A34A' : '#fff',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {entry.added && <CheckOutlined />}
                  </span>
                  {entry.added ? (
                    <FormattedMessage id="marketerView.recos.added" defaultMessage="Added to your Action plan" />
                  ) : (
                    <FormattedMessage id="marketerView.recos.add" defaultMessage="Add to your Action plan" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
    </ReadSection>
  );
};
